import React, { useState } from 'react'
import {
  Upload,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Play,
  Image,
  X,
  Send,
  Paperclip
} from 'lucide-react'
import { statusColors } from '../../data/mockData'

const deliverables = [
  {
    id: '1',
    campaign: 'Spring Wellness Launch',
    brand: 'MedTech Innovations',
    type: 'Instagram Reel',
    platform: 'instagram',
    status: 'revisions_requested',
    dueDate: '2026-02-15',
    fee: 5000,
    requirements: 'Create a 30-60 second Reel showcasing the wellness tracker in a day-in-the-life format.',
    feedback: [
      { from: 'brand', message: 'Love the energy! Please add the #ad disclosure at the beginning and mention the website URL verbally.', time: '2 hours ago' },
    ],
    draft: {
      version: 1,
      submittedAt: '2026-02-12',
      preview: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=600&fit=crop',
    },
  },
  {
    id: '2',
    campaign: 'Spring Wellness Launch',
    brand: 'MedTech Innovations',
    type: 'TikTok Video',
    platform: 'tiktok',
    status: 'pending',
    dueDate: '2026-02-20',
    fee: 5000,
    requirements: 'Create an educational TikTok about heart health monitoring. Include talking points about FDA clearance and EHR connectivity.',
    feedback: [],
    draft: null,
  },
  {
    id: '3',
    campaign: 'Heart Health Awareness',
    brand: 'CardioFirst',
    type: 'Instagram Carousel',
    platform: 'instagram',
    status: 'draft_submitted',
    dueDate: '2026-02-25',
    fee: 3500,
    requirements: '5-slide carousel educating about heart disease warning signs. Final slide should have CTA to download the app.',
    feedback: [],
    draft: {
      version: 1,
      submittedAt: '2026-02-10',
      preview: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400&h=400&fit=crop',
    },
  },
  {
    id: '4',
    campaign: 'Spring Wellness Launch',
    brand: 'MedTech Innovations',
    type: 'Instagram Stories',
    platform: 'instagram',
    status: 'approved',
    dueDate: '2026-02-18',
    fee: 2000,
    requirements: '3 Story slides: unboxing, setup tutorial, and daily use.',
    feedback: [
      { from: 'healthfluence', message: 'Compliance check passed. Great job with the disclosure!', time: '1 day ago' },
      { from: 'brand', message: 'Approved! Please post by Feb 18th.', time: '1 day ago' },
    ],
    draft: {
      version: 2,
      submittedAt: '2026-02-08',
      preview: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=600&fit=crop',
    },
  },
]

const statusLabels = {
  pending: { label: 'Not Started', color: 'bg-gray-100 text-gray-700', icon: Clock },
  draft_submitted: { label: 'Under Review', color: 'bg-blue-100 text-blue-700', icon: FileText },
  revisions_requested: { label: 'Revisions Needed', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  posted: { label: 'Posted', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
}

function CreatorDeliverables() {
  const [selectedDeliverable, setSelectedDeliverable] = useState(null)
  const [activeTab, setActiveTab] = useState('all')
  const [newMessage, setNewMessage] = useState('')

  const filteredDeliverables = activeTab === 'all'
    ? deliverables
    : deliverables.filter(d => d.status === activeTab)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Deliverables</h1>
        <p className="text-gray-500">Track and submit your campaign content</p>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Not Started' },
          { id: 'draft_submitted', label: 'Under Review' },
          { id: 'revisions_requested', label: 'Revisions Needed' },
          { id: 'approved', label: 'Approved' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-hf-teal-100 text-hf-teal-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            {tab.id !== 'all' && (
              <span className="ml-2 text-xs">
                ({deliverables.filter(d => d.status === tab.id).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Deliverables List */}
      <div className="space-y-4">
        {filteredDeliverables.map((del) => {
          const status = statusLabels[del.status]
          const StatusIcon = status.icon

          return (
            <div
              key={del.id}
              className="card hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedDeliverable(del)}
            >
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Preview */}
                <div className="w-full lg:w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {del.draft?.preview ? (
                    <img
                      src={del.draft.preview}
                      alt="Draft preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Image className="w-8 h-8" />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{del.type}</h3>
                      <p className="text-sm text-gray-500">{del.campaign} • {del.brand}</p>
                    </div>
                    <span className={`badge ${status.color} flex items-center`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {status.label}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{del.requirements}</p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        Due {new Date(del.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <span className="font-medium text-gray-900">${del.fee.toLocaleString()}</span>
                    </div>

                    {del.status === 'pending' && (
                      <button className="btn-primary text-sm py-1.5">
                        <Upload className="w-4 h-4 mr-1" />
                        Upload Draft
                      </button>
                    )}
                    {del.status === 'revisions_requested' && (
                      <button className="btn-primary text-sm py-1.5">
                        Submit Revision
                      </button>
                    )}
                    {del.feedback.length > 0 && (
                      <span className="flex items-center text-sm text-hf-teal-600">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {del.feedback.length} message{del.feedback.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {filteredDeliverables.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No deliverables found</h3>
          <p className="text-gray-500">Deliverables matching this filter will appear here.</p>
        </div>
      )}

      {/* Deliverable Detail Modal */}
      {selectedDeliverable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => setSelectedDeliverable(null)}
          />
          <div className="relative bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedDeliverable.type}</h2>
                <p className="text-sm text-gray-500">{selectedDeliverable.campaign}</p>
              </div>
              <button
                onClick={() => setSelectedDeliverable(null)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Left: Preview & Upload */}
                <div>
                  <div className="aspect-[9/16] bg-gray-100 rounded-xl overflow-hidden mb-4">
                    {selectedDeliverable.draft?.preview ? (
                      <img
                        src={selectedDeliverable.draft.preview}
                        alt="Draft preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                        <Upload className="w-12 h-12 mb-3" />
                        <p className="text-sm">No draft uploaded yet</p>
                      </div>
                    )}
                  </div>
                  {selectedDeliverable.status === 'pending' && (
                    <button className="btn-primary w-full">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Draft
                    </button>
                  )}
                  {selectedDeliverable.status === 'revisions_requested' && (
                    <button className="btn-primary w-full">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload New Version
                    </button>
                  )}
                  {selectedDeliverable.status === 'approved' && (
                    <button className="btn-success w-full">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Posted
                    </button>
                  )}
                </div>

                {/* Right: Requirements & Feedback */}
                <div className="space-y-6">
                  {/* Requirements */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Requirements</h3>
                    <p className="text-gray-600 text-sm">{selectedDeliverable.requirements}</p>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Due Date</p>
                      <p className="font-medium text-gray-900">
                        {new Date(selectedDeliverable.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Fee</p>
                      <p className="font-medium text-gray-900">${selectedDeliverable.fee.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Feedback Thread */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Feedback</h3>
                    {selectedDeliverable.feedback.length > 0 ? (
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {selectedDeliverable.feedback.map((msg, i) => (
                          <div key={i} className={`p-3 rounded-lg ${msg.from === 'brand' ? 'bg-blue-50' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-600 capitalize">{msg.from}</span>
                              <span className="text-xs text-gray-400">{msg.time}</span>
                            </div>
                            <p className="text-sm text-gray-700">{msg.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No feedback yet. Submit your draft to receive feedback.</p>
                    )}

                    {/* Reply Input */}
                    {selectedDeliverable.feedback.length > 0 && (
                      <div className="mt-4 flex gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type a message..."
                          className="input flex-1"
                        />
                        <button className="btn-primary px-4">
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreatorDeliverables
